import {useOrganization} from "crqlar-react-common/dist/state/auth.reducer";
import {useTranslation} from "react-i18next";
import {countryName} from "../../components/countryLabels";
import type {InsightBookingActivityValueV4, InsightDistributionValueV4} from "../../models/crm-v4";
import type {InsightsLoading} from "../../kit/insightsLoading";
import CrmBucketsCard, {bucketRows, type BucketRow} from "./CrmBucketsCard";
import CrmDistributionCard, {type DistributionRow} from "./CrmDistributionCard";
import CrmShareCard from "./CrmShareCard";
import {currentCalendarMonthKey} from "../base/InsightCurrentMonthHighlight";

/** Bars a ranked card draws before the tail becomes unreadable; the table still lists everything. */
const RANKED_CHART_ROWS = 8;

type Translate = (key: string, options?: Record<string, unknown>) => string;

/** Country rows carry their own flag; "UNKNOWN" is a bucket, not a place, so it gets none. */
function countryRows(items: InsightDistributionValueV4[], locale: string, t: Translate): DistributionRow[] {
    return items.map(item => ({
        key: item.key,
        label: item.key === "UNKNOWN" ? t("Unknown") : countryName(item.key, locale),
        countryCode: item.key === "UNKNOWN" ? undefined : item.key,
        value: item.value,
        compareValue: item.compareValue,
    }));
}

interface GuestProfileCardProps {
    items: InsightDistributionValueV4[];
    loading?: InsightsLoading;
    comparing: boolean;
}

const ACCOUNT_TYPE_LABELS: Record<string, string> = {
    INDIVIDUAL: "Individual guest",
    CORPORATE: "Corporate",
    WHOLESALER: "Wholesaler",
    NEWSLETTER_ONLY: "Newsletter only",
    STAFF: "Staff",
    OTHER: "Other",
    UNKNOWN: "Unknown",
};

const DISTANCE_LABELS: Record<string, string> = {
    UNDER_50: "Under 50 km",
    "50_149": "50–149 km",
    "150_499": "150–499 km",
    "500_1499": "500–1,499 km",
    "1500_PLUS": "1,500+ km",
    UNKNOWN: "Unknown",
};

const BOOKING_LABELS: Record<string, string> = {
    "1": "1",
    "2": "2",
    "3": "3",
    "4": "4",
    "5": "5",
    "6_PLUS": "6+",
};

const SERVICE_TYPE_BUCKETS = ["1", "2", "3_PLUS"];

export function ReservationAccountTypeCard({
    items,
    loading,
    comparing,
}: {items: InsightDistributionValueV4[]; loading?: InsightsLoading; comparing: boolean}) {
    const {t} = useTranslation();

    return (
        <CrmShareCard
            title={t("Reservation account type")}
            hint={t("Room reservations in the selected period, grouped by the primary booker's account type")}
            rows={items.map(item => ({
                key: item.key,
                label: t(ACCOUNT_TYPE_LABELS[item.key] ?? item.key),
                value: item.value,
                compareValue: item.compareValue,
            }))}
            loading={loading}
            comparing={comparing}
            format={value => t("{{count}} reservations", {count: value})}
            valueLabel={t("Reservations")}
            empty={t("No data in the selected period")}
        />
    );
}

export function BookingActivityCard({
    items,
    loading,
    comparing,
}: {items: InsightBookingActivityValueV4[]; loading?: InsightsLoading; comparing: boolean}) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const compactCount = new Intl.NumberFormat(locale, {notation: "compact", maximumFractionDigits: 1});
    const hasComparison = comparing && items.some(item => item.compareValue != null);
    const serviceLabel = (key: string) => key === "1"
        ? t("1 service type")
        : key === "2" ? t("2 service types") : t("3+ service types");
    const figure = (bookingBucket: string, serviceBucket: string, comparison: boolean) => {
        const item = items.find(row => row.bookingBucket === bookingBucket && row.serviceTypeBucket === serviceBucket);

        return (comparison ? item?.compareValue : item?.value) ?? 0;
    };
    const figures = (bookingBucket: string, comparison: boolean) => Object.fromEntries(
        SERVICE_TYPE_BUCKETS.map(serviceBucket => [serviceBucket, figure(bookingBucket, serviceBucket, comparison)]),
    );
    const rows: BucketRow[] = Object.keys(BOOKING_LABELS).map(bookingBucket => ({
        key: bookingBucket,
        label: BOOKING_LABELS[bookingBucket],
        values: figures(bookingBucket, false),
        compareValues: hasComparison ? figures(bookingBucket, true) : undefined,
    }));

    return (
        <CrmBucketsCard
            title={t("Booking activity")}
            hint={t("Lifetime bookings made by this period's primary room bookers, stacked by how many service types they used")}
            rows={rows}
            bucketLabel={t("Lifetime bookings per booker")}
            parts={SERVICE_TYPE_BUCKETS.map(key => ({id: key, label: serviceLabel(key)}))}
            loading={loading}
            comparing={comparing}
            format={value => t("{{count}} bookers", {count: value})}
            formatTick={value => compactCount.format(value)}
            empty={t("No data in the selected period")}
        />
    );
}

export function LanguagesCard({items, loading, comparing}: GuestProfileCardProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const languageNames = new Intl.DisplayNames([locale], {type: "language"});
    const displayName = (names: Intl.DisplayNames, key: string) => {
        try {
            return names.of(key) ?? key;
        } catch {
            return key;
        }
    };
    const languageLabel = (key: string) => key === "UNKNOWN" ? t("Unknown") : displayName(languageNames, key);

    return (
        <CrmDistributionCard
            title={t("Languages used")}
            hint={t("Languages on the profiles of guests who arrived for a room stay; connector variants are normalized")}
            rows={items.map(item => ({
                key: item.key,
                label: languageLabel(item.key),
                value: item.value,
                compareValue: item.compareValue,
            }))}
            loading={loading}
            comparing={comparing}
            format={value => t("{{count}} guests", {count: value})}
            valueLabel={t("Guests")}
            limit={RANKED_CHART_ROWS}
            empty={t("No data in the selected period")}
        />
    );
}

export function BirthdayMonthsCard({items, loading, comparing}: GuestProfileCardProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;
    const monthLabel = (key: string) => {
        const month = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"].indexOf(key);

        return month < 0 ? key : new Intl.DateTimeFormat(locale, {month: "short"}).format(new Date(2020, month, 1));
    };

    // Two periods rarely hold the same number of guests, so the months are read as shares of their own period.
    const share = (value: number, total: number) => total > 0 ? value * 100 / total : 0;
    const selectedTotal = items.reduce((sum, item) => sum + item.value, 0);
    const comparisonTotal = items.reduce((sum, item) => sum + (item.compareValue ?? 0), 0);

    return (
        <CrmBucketsCard
            title={t("Birthday months")}
            hint={t("Birth month distribution of guests arriving in each period; percentages use only guests with a plausible birth date")}
            rows={bucketRows(
                items.map(item => ({
                    key: item.key,
                    value: share(item.value, selectedTotal),
                    compareValue: item.compareValue == null ? undefined : share(item.compareValue, comparisonTotal),
                })),
                monthLabel,
            )}
            loading={loading}
            comparing={comparing}
            highlighted={monthLabel(currentCalendarMonthKey())}
            format={value => `${value.toLocaleString(locale, {maximumFractionDigits: 1})}%`}
            formatTick={value => `${value.toLocaleString(locale, {maximumFractionDigits: 0})}%`}
            valueLabel={t("Share")}
            empty={t("No data in the selected period")}
        />
    );
}

export function RevenueByCountryCard({items, loading, comparing}: GuestProfileCardProps) {
    const {t, i18n} = useTranslation();
    const currency = useOrganization()?.currencyCode || "EUR";
    const locale = i18n.resolvedLanguage || i18n.language;
    const money = new Intl.NumberFormat(locale, {style: "currency", currency, maximumFractionDigits: 0});

    return (
        <CrmDistributionCard
            title={t("Revenue by country")}
            hint={t("Lifetime folio revenue from this period's adult individual room guests, grouped by home country")}
            rows={countryRows(items, locale, t)}
            loading={loading}
            comparing={comparing}
            format={value => money.format(value)}
            valueLabel={t("Revenue")}
            limit={RANKED_CHART_ROWS}
            empty={t("No data in the selected period")}
        />
    );
}

export function CountriesCard({items, loading, comparing}: GuestProfileCardProps) {
    const {t, i18n} = useTranslation();
    const locale = i18n.resolvedLanguage || i18n.language;

    return (
        <CrmDistributionCard
            title={t("Countries")}
            hint={t("Home countries of the guests who arrived for a room stay; this does not depend on geocoding")}
            rows={countryRows(items, locale, t)}
            loading={loading}
            comparing={comparing}
            format={value => t("{{count}} guests", {count: value})}
            valueLabel={t("Guests")}
            limit={RANKED_CHART_ROWS}
            empty={t("No data in the selected period")}
        />
    );
}

export function TopCitiesCard({items, loading, comparing}: GuestProfileCardProps) {
    const {t} = useTranslation();

    return (
        <CrmDistributionCard
            title={t("Top cities")}
            hint={t("Home cities of room-stay guests; resolved cache entries merge spelling variants under a canonical city name")}
            rows={items.map(item => ({
                key: item.key,
                label: item.key === "UNKNOWN" ? t("Unknown") : item.key,
                // "Salzburg, AT" — the trailing code is what puts a flag on the row.
                countryCode: item.key.match(/,\s*([A-Za-z]{2})$/)?.[1],
                value: item.value,
                compareValue: item.compareValue,
            }))}
            loading={loading}
            comparing={comparing}
            format={value => t("{{count}} guests", {count: value})}
            valueLabel={t("Guests")}
            limit={RANKED_CHART_ROWS}
            empty={t("No data in the selected period")}
        />
    );
}

export function TravelDistanceCard({items, loading, comparing}: GuestProfileCardProps) {
    const {t} = useTranslation();

    return (
        <CrmDistributionCard
            title={t("Travel distance")}
            hint={t("Straight-line distance from the property to each guest's home city; unresolved addresses remain visible as Unknown")}
            // Distance bands are ordered, so they stay in the order the API sends rather than by size.
            rows={items.map(item => ({
                key: item.key,
                label: t(DISTANCE_LABELS[item.key] ?? item.key),
                value: item.value,
                compareValue: item.compareValue,
            }))}
            loading={loading}
            comparing={comparing}
            format={value => t("{{count}} guests", {count: value})}
            valueLabel={t("Guests")}
            limit={RANKED_CHART_ROWS}
            empty={t("No data in the selected period")}
        />
    );
}
